import React, { useCallback, useEffect, useRef } from 'react';
import { useLocale } from '../../../locales';

interface SaveSuccessModalProps {
  message: string;
  onContinueEditing: () => void;
  onRedirect: () => void;
}

const SaveSuccessModal: React.FC<SaveSuccessModalProps> = ({
  message,
  onContinueEditing,
  onRedirect,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  const handleContinueEditing = useCallback(() => {
    onContinueEditing();
  }, [onContinueEditing]);

  const handleRedirect = useCallback(() => {
    onRedirect();
  }, [onRedirect]);

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" />
      <div className="qe-modal__content qe-modal__content--success" ref={contentRef} tabIndex={-1}>
        <div className="qe-modal__success-container">
          {/* Success Icon */}
          <div className="qe-modal__success-icon-wrapper">
            <svg
              className="qe-modal__success-icon-large"
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="40" cy="40" r="36" stroke="currentColor" strokeWidth="4" opacity="0.2"/>
              <circle cx="40" cy="40" r="28" fill="currentColor" opacity="0.1"/>
              <path
                d="M26 40L35 49L54 30"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="qe-modal__success-check"
              />
            </svg>
          </div>

          {/* Success Message */}
          <div className="qe-modal__success-heading">
            <h2 className="qe-modal__success-title">{t("saveSuccess.title")}</h2>
            <p className="qe-modal__success-message">{message}</p>
          </div>

          {/* Action Cards */}
          <div className="qe-modal__action-cards">
            <button
              type="button"
              className="qe-modal__action-card qe-modal__action-card--continue"
              onClick={handleContinueEditing}
            >
              <div className="qe-modal__action-card-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M25.333 5.333H22.666V3.999C22.666 3.646 22.526 3.307 22.276 3.057C22.025 2.806 21.687 2.666 21.333 2.666H20C19.646 2.666 19.307 2.806 19.057 3.057C18.806 3.307 18.666 3.646 18.666 3.999V5.333H13.333V3.999C13.333 3.646 13.193 3.307 12.943 3.057C12.692 2.806 12.354 2.666 12 2.666H10.666C10.313 2.666 9.974 2.806 9.724 3.057C9.473 3.307 9.333 3.646 9.333 3.999V5.333H6.666C6.313 5.333 5.974 5.473 5.724 5.724C5.473 5.974 5.333 6.313 5.333 6.666V25.333C5.333 25.686 5.473 26.025 5.724 26.276C5.974 26.526 6.313 26.666 6.666 26.666H25.333C25.686 26.666 26.025 26.526 26.276 26.276C26.526 26.025 26.666 25.686 26.666 25.333V6.666C26.666 6.313 26.526 5.974 26.276 5.724C26.025 5.473 25.686 5.333 25.333 5.333ZM18.666 22.666H13.333V20H18.666V22.666ZM22.666 17.333H9.333V14.666H22.666V17.333ZM22.666 10.666H9.333V7.999H22.666V10.666Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div className="qe-modal__action-card-content">
                <h3 className="qe-modal__action-card-title">{t("saveSuccess.continueEditing")}</h3>
                <p className="qe-modal__action-card-desc">
                  {t("saveSuccess.continueEditingDesc")}
                </p>
              </div>
              <div className="qe-modal__action-card-arrow">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 4L12 8L8 12M12 8H4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>

            <button
              type="button"
              className="qe-modal__action-card qe-modal__action-card--redirect"
              onClick={handleRedirect}
            >
              <div className="qe-modal__action-card-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 16H28M28 16L20 8M28 16L20 24"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="qe-modal__action-card-content">
                <h3 className="qe-modal__action-card-title">{t("saveSuccess.goToQuestions")}</h3>
                <p className="qe-modal__action-card-desc">
                  {t("saveSuccess.goToQuestionsDesc")}
                </p>
              </div>
              <div className="qe-modal__action-card-arrow">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 4L12 8L8 12M12 8H4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveSuccessModal;
